/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Stack,
  TextField,
  Switch,
  Button,
  MenuItem,
  Typography,
  Select,
  FormControl,
  FormControlLabel,
  InputLabel,
  SelectChangeEvent,
  useTheme,
  Box,
} from "@mui/material";
import { ChevronIcon } from "~components/Icons/index";
import { useSocketContext } from "~contexts/Socket";
import { useStreamContext } from "~contexts/Stream";
import {
  getProductsTimeline,
  getProductsCache,
  updateProductsTimeline,
  updateProductsTimelineKeyframeInterval,
  deleteProductsTimelineKeyframe,
} from "~api/stream";
import { STREAMS } from "~helpers/constants";
import type {
  Button as StreamButton,
  Product as StreamProduct,
  TKeyframe,
  TTimelineResponse,
} from "~api/stream/interfaces";
import BrandSelector from "~components/atoms/BrandSelector";

const PRODUCT_PLACEHOLDER_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="70" height="70" viewBox="0 0 70 70"><rect width="70" height="70" rx="8" fill="#E0E0E0"/><path d="M21 47l10-12 8 9 6-7 8 10H21z" fill="#BDBDBD"/><circle cx="28" cy="26" r="5" fill="#BDBDBD"/></svg>'
  );

const Stream: React.FC = () => {
  const theme: any = useTheme();
  const { isReaction, setIsReaction, updateStreamSocket } = useSocketContext();
  const { stream, updateStream, currentStream } = useStreamContext();
  const [liveStatus, setLiveStatus] = useState<string>();
  const [productTime, setProductTime] = useState<boolean>(false);
  const [rerunStartTime, setRerunStartTime] = useState<number>(0);
  const [firstRerunTime, setFirstRerunTime] = useState<number>(0);
  const [videoLinkOrId, setVideoLinkOrId] = useState<string>("");
  const [chatEnabled, setChatEnabled] = useState<boolean>(false);
  const [videoStatus, setVideoStatus] = useState<boolean>(false);
  const [measureEnabled, setMeasureEnabled] = useState<boolean>(false);
  const [timelineData, setTimelineData] = useState<TTimelineResponse>({
    enabled: false,
    keyframes: [],
  });
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [savingKeyframeId, setSavingKeyframeId] = useState<string | null>(null);
  const [editingIntervals, setEditingIntervals] = useState<Record<string, string>>({});
  const [cachedButtons, setCachedButtons] = useState<StreamButton[]>([]);
  const [imageFallbackByKey, setImageFallbackByKey] = useState<Record<string, boolean>>({});

  const streamId = currentStream
    ? STREAMS.find((stream) => stream.name === currentStream.name)?.id
    : null;

  useEffect(() => {
    if (currentStream) {
      // Reset local form state while loading new stream
      setLiveStatus(undefined);
      setChatEnabled(false);
      setMeasureEnabled(false);
      setVideoLinkOrId("");
      setRerunStartTime(0);
      setFirstRerunTime(0);
      setVideoStatus(false);
      setTimelineData({ enabled: false, keyframes: [] });
      setProductTime(false);
      updateTimelineData();
    }
  }, [currentStream]);

  useEffect(() => {
    if (stream) {
      setIsReaction(Boolean(stream.has_reaction));
      setChatEnabled(Boolean(stream.has_chat));
      setMeasureEnabled(Boolean(stream.has_measure));
      setVideoLinkOrId(stream.video?.vimeo_id ?? stream.video?.url ?? stream.stream_url ?? "");
      setLiveStatus(String(stream.video?.isRerun ?? 0));
      setRerunStartTime(stream.video?.rerunStartTime ?? 0);
    }
  }, [setIsReaction, stream]);

  useEffect(() => {
    let mounted = true;
    if (!currentStream?.name) {
      setCachedButtons([]);
      return;
    }

    getProductsCache(currentStream.name)
      .then((data) => {
        if (!mounted) return;
        setCachedButtons(
          Array.isArray(data?.buttons) ? (data.buttons as StreamButton[]) : []
        );
      })
      .catch(() => {
        if (!mounted) return;
        setCachedButtons([]);
      });

    return () => {
      mounted = false;
    };
  }, [currentStream?.name]);

  const sortKeyframesByLive = (keyframes: TKeyframe[] = []) =>
    [...keyframes].sort((a, b) => a.live.displayAt - b.live.displayAt);

  const updateFirstStartOnTimeline = async (
    timestampInSeconds: number,
    enabled: boolean
  ) => {
    if (liveStatus !== "1" || !streamId) return;
    await updateProductsTimeline(streamId, {
      timestamp: timestampInSeconds * 1000,
      enabled,
    });
  };

  const updateTimelineData = async () => {
    if (!streamId) return;
    setTimelineLoading(true);
    try {
      const timeline = await getProductsTimeline(streamId);
      const keyframes = timeline?.keyframes || [];
      const chronological = sortKeyframesByLive(keyframes);
      const firstRerunSecond = chronological.length
        ? parseInt(String(chronological[0].rerun.displayAt / 1000), 10)
        : 0;

      setTimelineData({ enabled: Boolean(timeline?.enabled), keyframes });
      setFirstRerunTime(firstRerunSecond);
      setProductTime(Boolean(timeline?.enabled));
    } catch (e) {
      setTimelineData({ enabled: false, keyframes: [] });
      setFirstRerunTime(0);
      setProductTime(false);
    } finally {
      setTimelineLoading(false);
    }
  };

  const timelineRows = useMemo(() => {
    // Use the same source used by frontend runtime: products-cache.
    const providerImageById = new Map<string, string>();
    const vtexImageById = new Map<string, string>();
    const sourceButtons = [...(cachedButtons || [])];

    for (const button of sourceButtons as StreamButton[]) {
      for (const product of (button.products || []) as StreamProduct[]) {
        const image =
          product.images?.thumbs?.[0] ||
          product.images?.std?.[0] ||
          product.images?.hd?.[0];

        const providerId = String(product.provider?.productId || "");
        const vtexId = String(product.id || "");

        if (image && providerId && !providerImageById.has(providerId)) {
          providerImageById.set(providerId, image);
        }
        if (image && vtexId && !vtexImageById.has(vtexId)) {
          vtexImageById.set(vtexId, image);
        }
      }
    }

    const keyframes = timelineData.keyframes;
    if (!keyframes.length) return [];

    const chronological = sortKeyframesByLive(keyframes);
    const chronologicalIndexById = new Map<string, number>();
    chronological.forEach((keyframe, index) => {
      chronologicalIndexById.set(keyframe.id, index);
    });

    return chronological.map((keyframe) => {
      const chronologicalIndex = chronologicalIndexById.get(keyframe.id) ?? 0;
      const previousChronological = chronological[chronologicalIndex - 1];
      const intervalMs =
        chronologicalIndex === 0
          ? 0
          : keyframe.live.displayAt - previousChronological.live.displayAt;
      return {
        keyframe,
        index: chronologicalIndex,
        isFirstChronological: chronologicalIndex === 0,
        intervalSeconds: parseInt(String(intervalMs / 1000), 10),
        displaySecond: parseInt(String(keyframe.rerun.displayAt / 1000), 10),
        products: Array.from(
          new Set(keyframe.products.map((product) => String(product.id_product)))
        ).map((id) => ({
          id,
          image: providerImageById.get(id) || vtexImageById.get(id) || null,
        })),
      };
    });
  }, [cachedButtons, timelineData.keyframes]);

  const saveKeyframeInterval = async (keyframeId: string) => {
    if (!streamId) return;
    const value = Number(editingIntervals[keyframeId]);
    if (!Number.isFinite(value) || value < 0) return;
    const row = timelineRows.find((item) => item.keyframe.id === keyframeId);
    if (!row) return;

    setSavingKeyframeId(keyframeId);
    try {
      if (row.isFirstChronological) {
        await updateFirstStartOnTimeline(value, productTime);
        setFirstRerunTime(value);
      } else {
        await updateProductsTimelineKeyframeInterval(streamId, keyframeId, {
          intervalSeconds: value,
        });
      }
      await updateTimelineData();
      setEditingIntervals((prev) => {
        const next = { ...prev };
        delete next[keyframeId];
        return next;
      });
    } finally {
      setSavingKeyframeId(null);
    }
  };

  const deleteKeyframe = async (keyframeId: string) => {
    if (!streamId) return;
    setSavingKeyframeId(keyframeId);
    try {
      await deleteProductsTimelineKeyframe(streamId, keyframeId);
      await updateTimelineData();
      setEditingIntervals((prev) => {
        const next = { ...prev };
        delete next[keyframeId];
        return next;
      });
    } finally {
      setSavingKeyframeId(null);
    }
  };

  const getVideoStatus = async () => {
    const vimeoApi = `https://api.vimeo.com/me/videos/${videoLinkOrId}`;
    const options = {
      headers: { Authorization: "Bearer 3b132d0d8870c56731bab720ff6d6044" },
    };

    return fetch(vimeoApi, options)
      .then((res) => res.json())
      .then((response) => !response.error);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isRerun = Number(liveStatus);
    const isVideoValid = isRerun ? true : await getVideoStatus();

    if (!stream || !currentStream) {
      return;
    }

    if (!isVideoValid && !isRerun) {
      setVideoStatus(true);
      return;
    }

    const data = {
      video_url: videoLinkOrId,
      vimeo_id: isRerun ? null : videoLinkOrId,
      is_rerun: Number(liveStatus),
      rerun_start_time: rerunStartTime,
      has_chat: +chatEnabled,
      has_reaction: +isReaction,
      has_measure: +measureEnabled,
      is_live: stream.is_live ?? 0,
      sales_channel: stream.sales_channel ?? currentStream.sales_channel,
    };

    setVideoStatus(false);
    await updateFirstStartOnTimeline(firstRerunTime, productTime);
    updateStream(data);
    updateStreamSocket(data);
  };

  // Se não há brand selecionada, mostrar mensagem
  if (!currentStream) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          minHeight: "60vh",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.grey[0],
          }}
          variant="h1"
        >
          bem-vindo ao ambiente Somalive
        </Typography>
        <Typography
          sx={{
            color: theme.palette.grey[600],
            fontSize: "15px",
            marginBottom: "24px",
          }}
        >
          comece a configurar sua live pelo menu ao lado após selecionar uma marca
        </Typography>
        <BrandSelector invertColors={true} />
      </Box>
    );
  }

  // Se está carregando, mostrar loading
  if (!stream && currentStream) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <Typography sx={{ color: theme.palette.grey[0] }}>
          carregando...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid item>
        <Typography variant="h1" sx={{ fontSize: "30px !important" }}>transmissão</Typography>
        <Typography variant="body2" sx={{ mb: "40px", fontSize: "15px" }}>
          não esqueça de salvar as alterações antes de sair
        </Typography>
      </Grid>

      <Grid item>
        <form onSubmit={handleSubmit}>
          <Stack sx={{ alignItems: "flex-start", mt: 7 }}>
            <FormControl fullWidth>
              <InputLabel>selecione uma opção</InputLabel>
              <Select
                label="selecione uma opção"
                value={`${liveStatus || ""}`}
                IconComponent={ChevronIcon}
                onChange={(event: SelectChangeEvent) => {
                  setLiveStatus(event.target.value);
                }}
              >
                <MenuItem value="0">ao vivo</MenuItem>
                <MenuItem value="1">reprise</MenuItem>
              </Select>
            </FormControl>

            {liveStatus !== undefined ? (
              <FormControl sx={{ mt: 1.5, mb: 1.5 }} fullWidth>
                <TextField
                  error={videoStatus}
                  helperText={
                    videoStatus && "O ID não é válido para uma transmissão ao vivo"
                  }
                  label="ID de transmissão"
                  defaultValue={videoLinkOrId}
                  onChange={(e) => setVideoLinkOrId(e.target.value)}
                />
              </FormControl>
            ) : null}

            {liveStatus === "1" ? (
              <>
                <FormControl fullWidth>
                  <TextField
                    label="iniciar vídeo no segundo..."
                    type="number"
                    defaultValue={rerunStartTime}
                    onChange={(e) => {
                      setRerunStartTime(Number(e.target.value));
                    }}
                  />
                </FormControl>
              </>
            ) : null}

            <Stack
              sx={{
                alignItems: "flex-start",
                mt: liveStatus === "0" ? 5.5 : 7,
              }}
            >
              {liveStatus !== undefined ? (
                <>
                  <FormControl sx={{ mb: 1.5 }}>
                    <FormControlLabel
                      control={<Switch name="chat" />}
                      label="chat"
                      labelPlacement="start"
                      checked={chatEnabled}
                      onClick={() => {
                        setChatEnabled(!chatEnabled);
                      }}
                    />
                  </FormControl>

                  <FormControl sx={{ mb: 1.5 }}>
                    <FormControlLabel
                      control={<Switch name="reação" />}
                      label="reação"
                      labelPlacement="start"
                      checked={isReaction}
                      onClick={() => {
                        setIsReaction(!isReaction);
                      }}
                    />
                  </FormControl>

                </>
              ) : null}

              {liveStatus === "1" ? (
                <FormControl>
                  <FormControlLabel
                    control={<Switch name="produto da vez" />}
                    label="produto da vez"
                    labelPlacement="start"
                    checked={productTime}
                    onChange={async (_event, checked) => {
                      setProductTime(checked);
                      await updateFirstStartOnTimeline(firstRerunTime, checked);
                      await updateTimelineData();
                    }}
                  />
                </FormControl>
              ) : null}
            </Stack>

            {liveStatus === "1" && productTime && (
              <FormControl sx={{ mt: 7 }} fullWidth>
                <TextField
                  type="number"
                  value={firstRerunTime}
                  onChange={(e) => setFirstRerunTime(Number(e.target.value))}
                  onBlur={async () => {
                    if (productTime) {
                      await updateFirstStartOnTimeline(firstRerunTime, productTime);
                      await updateTimelineData();
                    }
                  }}
                  label="iniciar produto da vez no segundo..."
                />
              </FormControl>
            )}

            {liveStatus === "1" && productTime && (
              <Stack sx={{ width: "100%", mt: 2 }} spacing={1.5}>
                <Typography variant="body2">pushes programados</Typography>
                {timelineLoading ? (
                  <Typography variant="body2" color={theme.palette.grey[500]}>
                    carregando timeline...
                  </Typography>
                ) : null}
                {!timelineLoading && !timelineRows.length ? (
                  <Typography variant="body2" color={theme.palette.grey[500]}>
                    nenhum push encontrado para essa stream
                  </Typography>
                ) : null}
                {!timelineLoading && timelineRows.length > 0 && (
                  <Stack spacing={1.5} sx={{ maxHeight: 580, overflowY: "auto", pr: 1 }}>
                    {timelineRows.map(
                      ({
                        keyframe,
                        index,
                        isFirstChronological,
                        intervalSeconds,
                        displaySecond,
                        products,
                      }) => {
                      const isEditing = typeof editingIntervals[keyframe.id] !== "undefined";
                      const isSaving = savingKeyframeId === keyframe.id;
                      return (
                        <Stack
                          key={keyframe.id}
                          direction="row"
                          spacing={1}
                          sx={{
                            width: "100%",
                            alignItems: "center",
                            border: `1px solid ${theme.palette.grey[700]}`,
                            borderRadius: 1,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ minWidth: 24 }}>{index + 1}</Typography>
                          {isEditing ? (
                            <TextField
                              type="number"
                              size="small"
                              label={isFirstChronological ? "início (s)" : "intervalo (s)"}
                              value={editingIntervals[keyframe.id]}
                              onChange={(e) =>
                                setEditingIntervals((prev) => ({
                                  ...prev,
                                  [keyframe.id]: e.target.value,
                                }))
                              }
                              sx={{ width: 160 }}
                            />
                          ) : (
                            <Typography sx={{ minWidth: 140 }}>
                              {isFirstChronological
                                ? `início: ${displaySecond}s`
                                : `intervalo: ${intervalSeconds}s`}
                            </Typography>
                          )}
                          <Typography sx={{ minWidth: 180 }}>
                            exibe no segundo: {displaySecond}s
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ flex: 1, flexWrap: "wrap" }}>
                            {products.length ? (
                              products.map((product) => {
                                const imageKey = `${keyframe.id}-${product.id}`;
                                const useFallback =
                                  imageFallbackByKey[imageKey] || !product.image;

                                return (
                                <Stack
                                  key={imageKey}
                                  direction="column"
                                  spacing={0.5}
                                  sx={{ alignItems: "center", minWidth: 78 }}
                                >
                                  <Box
                                    component="img"
                                    src={useFallback ? PRODUCT_PLACEHOLDER_IMAGE : product.image || ""}
                                    alt="produto"
                                    onError={() =>
                                      setImageFallbackByKey((prev) => ({
                                        ...prev,
                                        [imageKey]: true,
                                      }))
                                    }
                                    sx={{
                                      width: "auto",
                                      height: 70,
                                      maxWidth: 70,
                                      borderRadius: 0.5,
                                      objectFit: "cover",
                                    }}
                                  />
                                  <Typography variant="body2">{product.id}</Typography>
                                </Stack>
                              )})
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Stack>
                          {isEditing ? (
                            <Button
                              size="small"
                              disabled={isSaving}
                              onClick={() => saveKeyframeInterval(keyframe.id)}
                            >
                              salvar
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              disabled={isSaving}
                              onClick={() =>
                                setEditingIntervals((prev) => ({
                                  ...prev,
                                  [keyframe.id]:
                                    isFirstChronological
                                      ? String(displaySecond)
                                      : String(intervalSeconds),
                                }))
                              }
                            >
                              editar
                            </Button>
                          )}
                          <Button
                            size="small"
                            disabled={isSaving}
                            color="error"
                            onClick={() => deleteKeyframe(keyframe.id)}
                          >
                            excluir
                          </Button>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            )}

            {liveStatus !== undefined ? (
              <Button
                fullWidth
                variant="secondaryButton"
                type="submit"
                sx={{ mt: theme.spacing(6.75) }}
              >
                salvar alterações
              </Button>
            ) : null}
          </Stack>
        </form>
      </Grid>
    </>
  );
};

export default Stream;
